/**
 * MobyCalendar™ — Reactive hooks for calendar data
 * 
 * Uses Dexie liveQuery for automatic re-rendering when calendar data changes.
 * All data is local-only (P2P synced) — no server queries.
 */

import { useLiveQuery } from 'dexie-react-hooks';
import { useMobyDB, useSession } from '@/components/provider';
import { useMemo, useCallback } from 'react';
import type { CalendarEventEntity, CalendarInviteEntity } from '@/moby/db';
import {
  type CalendarEvent,
  type CalendarAttendee,
  type CalendarReminder,
  type RecurrenceRule,
  type CalendarInvite,
  CalendarRSVP,
  EventVisibility,
  ReminderMethod,
  type CalendarSettings,
  DEFAULT_CALENDAR_SETTINGS,
} from '@/lib/calendar-types';

// ─── Entity ↔ Domain Converters ──────────────────────────────────────────────

/** Convert a Dexie entity to a domain CalendarEvent */
function entityToEvent(entity: CalendarEventEntity): CalendarEvent {
  return {
    id: entity.id,
    title: entity.title,
    description: entity.description,
    location: entity.location,
    startTime: entity.startTime,
    endTime: entity.endTime,
    allDay: entity.allDay === 1,
    timezone: entity.timezone,
    recurrence: entity.recurrence ? JSON.parse(entity.recurrence) as RecurrenceRule : undefined,
    recurringEventId: entity.recurringEventId,
    visibility: entity.visibility as EventVisibility,
    reminders: entity.reminders ? JSON.parse(entity.reminders) as CalendarReminder[] : [],
    organizerAddress: entity.organizerAddress,
    attendees: JSON.parse(entity.attendees) as CalendarAttendee[],
    groupAddress: entity.groupAddress,
    vectorClock: JSON.parse(entity.vectorClock) as Record<string, number>,
    createdAt: entity.createdAt,
    updatedAt: entity.updatedAt,
    deletedAt: entity.deletedAt,
    color: entity.color,
  };
}

/** Convert a domain CalendarEvent to a Dexie-compatible plain object */
function eventToEntity(event: CalendarEvent): Omit<CalendarEventEntity, keyof InstanceType<any>> {
  return {
    id: event.id,
    title: event.title,
    description: event.description,
    location: event.location,
    startTime: event.startTime,
    endTime: event.endTime,
    allDay: event.allDay ? 1 : 0,
    timezone: event.timezone,
    recurrence: event.recurrence ? JSON.stringify(event.recurrence) : undefined,
    recurringEventId: event.recurringEventId,
    visibility: event.visibility,
    reminders: JSON.stringify(event.reminders),
    organizerAddress: event.organizerAddress,
    attendees: JSON.stringify(event.attendees),
    groupAddress: event.groupAddress,
    vectorClock: JSON.stringify(event.vectorClock),
    createdAt: event.createdAt,
    updatedAt: event.updatedAt,
    deletedAt: event.deletedAt,
    color: event.color,
  } as any;
}

/** Convert a Dexie entity to a domain CalendarInvite */
function entityToInvite(entity: CalendarInviteEntity): CalendarInvite {
  return {
    id: entity.id,
    eventId: entity.eventId,
    senderAddress: entity.senderAddress,
    recipientAddress: entity.recipientAddress,
    rsvp: entity.rsvp as CalendarRSVP,
    sentAt: entity.sentAt,
    respondedAt: entity.respondedAt,
    deliveredP2P: entity.deliveredP2P === 1,
    pendingDeliveryId: entity.pendingDeliveryId,
    eventSnapshot: JSON.parse(entity.eventSnapshot),
  };
}

// ─── Hooks ───────────────────────────────────────────────────────────────────

/**
 * Get all non-deleted calendar events within a time range.
 * Default: current month ± 1 week buffer for edge-day rendering.
 */
export function useCalendarEvents(startMs?: number, endMs?: number) {
  const db = useMobyDB();
  
  // Default to current month with buffer
  const range = useMemo(() => {
    if (startMs !== undefined && endMs !== undefined) {
      return { start: startMs, end: endMs };
    }
    const now = new Date();
    const monthStart = new Date(now.getFullYear(), now.getMonth(), 1);
    const monthEnd = new Date(now.getFullYear(), now.getMonth() + 1, 0, 23, 59, 59, 999);
    // Buffer: 1 week before and after for events that span month boundaries
    const bufferMs = 7 * 24 * 60 * 60 * 1000;
    return {
      start: monthStart.getTime() - bufferMs,
      end: monthEnd.getTime() + bufferMs,
    };
  }, [startMs, endMs]);

  const events = useLiveQuery(
    async () => {
      const entities = await db.calendarEvents
        .where('[startTime+endTime]')
        .between(
          [range.start, Number.MIN_SAFE_INTEGER],
          [range.end, Number.MAX_SAFE_INTEGER],
        )
        .toArray();
      
      // Also include events that started before the range but end within it
      const overlapping = await db.calendarEvents
        .where('endTime')
        .above(range.start)
        .filter(e => e.startTime < range.start && !e.deletedAt)
        .toArray();
      
      // Merge and deduplicate
      const allEntities = new Map<string, CalendarEventEntity>();
      for (const e of [...entities, ...overlapping]) {
        if (!e.deletedAt) {
          allEntities.set(e.id, e);
        }
      }
      
      return Array.from(allEntities.values()).map(entityToEvent);
    },
    [range.start, range.end],
  );

  return events ?? [];
}

/**
 * Get the next N upcoming events from now.
 * Used by the Header component for "next meeting" display.
 */
export function useUpcomingEvents(limit: number = 5) {
  const db = useMobyDB();
  const now = Date.now();

  const events = useLiveQuery(
    async () => {
      const entities = await db.calendarEvents
        .where('startTime')
        .above(now)
        .filter(e => !e.deletedAt)
        .limit(limit)
        .sortBy('startTime');
      
      return entities.map(entityToEvent);
    },
    [now, limit],
  );

  return events ?? [];
}

/**
 * Get a window of nearby events (past + upcoming) sorted ascending by startTime.
 * Used by the header pill to cycle through recent/upcoming events.
 * Returns combined list plus the index of the first upcoming event (the default
 * cursor position). If there are no upcoming events, `upcomingStartIndex` is
 * `combined.length` (past the end) so callers fall back to the last past event.
 */
export function useNearbyEvents(pastLimit: number = 10, futureLimit: number = 10) {
  const db = useMobyDB();
  const now = Date.now();

  const combined = useLiveQuery(
    async () => {
      const [pastEntities, futureEntities] = await Promise.all([
        db.calendarEvents
          .where('startTime')
          .belowOrEqual(now)
          .filter(e => !e.deletedAt)
          .reverse()
          .limit(pastLimit)
          .sortBy('startTime'),
        db.calendarEvents
          .where('startTime')
          .above(now)
          .filter(e => !e.deletedAt)
          .limit(futureLimit)
          .sortBy('startTime'),
      ]);

      const all = [...pastEntities, ...futureEntities]
        .map(entityToEvent)
        .sort((a, b) => a.startTime - b.startTime);

      return all;
    },
    [now, pastLimit, futureLimit],
  );

  const list = combined ?? [];
  const upcomingStartIndex = list.findIndex(e => e.startTime > now);
  return {
    combined: list,
    upcomingStartIndex: upcomingStartIndex === -1 ? list.length : upcomingStartIndex,
  };
}

/**
 * Get all pending invites for the current user (received invites awaiting RSVP).
 */
export function usePendingInvites() {
  const db = useMobyDB();
  const session = useSession();

  const invites = useLiveQuery(
    async () => {
      if (!session?.address) return [];
      
      const entities = await db.calendarInvites
        .where('[recipientAddress+rsvp]')
        .equals([session.address, CalendarRSVP.PENDING])
        .toArray();
      
      return entities.map(entityToInvite);
    },
    [session?.address],
  );

  return invites ?? [];
}

/**
 * Get all invites for a specific event.
 */
export function useEventInvites(eventId: string) {
  const db = useMobyDB();

  const invites = useLiveQuery(
    async () => {
      const entities = await db.calendarInvites
        .where('eventId')
        .equals(eventId)
        .toArray();
      
      return entities.map(entityToInvite);
    },
    [eventId],
  );

  return invites ?? [];
}

/**
 * Get events for a specific day (for day-view or day-cell rendering).
 */
export function useDayEvents(dayStart: number, dayEnd: number) {
  const db = useMobyDB();

  const events = useLiveQuery(
    async () => {
      // Events that start on this day
      const startsOnDay = await db.calendarEvents
        .where('startTime')
        .between(dayStart, dayEnd)
        .filter(e => !e.deletedAt)
        .toArray();
      
      // Events that span across this day (started before, ends after start of day)
      const spansDay = await db.calendarEvents
        .where('endTime')
        .above(dayStart)
        .filter(e => e.startTime < dayStart && !e.deletedAt)
        .toArray();
      
      const allEntities = new Map<string, CalendarEventEntity>();
      for (const e of [...startsOnDay, ...spansDay]) {
        allEntities.set(e.id, e);
      }
      
      return Array.from(allEntities.values())
        .map(entityToEvent)
        .sort((a, b) => a.startTime - b.startTime);
    },
    [dayStart, dayEnd],
  );

  return events ?? [];
}

// ─── CRUD Operations ─────────────────────────────────────────────────────────

/**
 * Calendar event CRUD operations.
 * Returns stable callbacks for create/update/delete.
 */
export function useCalendarCRUD() {
  const db = useMobyDB();
  const session = useSession();

  const createEvent = useCallback(async (event: Omit<CalendarEvent, 'id' | 'vectorClock' | 'createdAt' | 'updatedAt' | 'organizerAddress'>): Promise<CalendarEvent> => {
    if (!session?.address) throw new Error('No session');
    
    const now = Date.now();
    // Ensure the organizer is included in the attendees list with 'accepted' RSVP.
    // Callers (e.g. NewEventForm) only pass invited recipients — the organizer
    // must always be present so both sender and receiver see matching attendee counts.
    const organizerAttendee: CalendarAttendee = {
      address: session.address,
      displayName: session.name || session.address.slice(0, 8),
      rsvp: CalendarRSVP.ACCEPTED,
      isOrganizer: true,
    };
    const hasOrganizer = event.attendees?.some(a => a.address === session.address);
    const attendeesWithOrganizer = hasOrganizer
      ? (event.attendees ?? [])
      : [organizerAttendee, ...(event.attendees ?? [])];

    const fullEvent: CalendarEvent = {
      ...event,
      attendees: attendeesWithOrganizer,
      id: crypto.randomUUID(),
      organizerAddress: session.address,
      vectorClock: { [session.address]: 1 },
      createdAt: now,
      updatedAt: now,
    };
    
    await db.calendarEvents.add(eventToEntity(fullEvent) as any);

    // Create invite records + pending deliveries for each attendee
    const attendees = fullEvent.attendees ?? [];

    // Resolve group name from DB if this event belongs to a group
    let groupName: string | undefined;
    if (fullEvent.groupAddress) {
      try {
        const groupChat = await db.chats.where('address').equals(fullEvent.groupAddress).first();
        groupName = groupChat?.name ?? undefined;
      } catch { /* ignore */ }
    }

    const eventSnapshot = JSON.stringify({
      title: fullEvent.title,
      startTime: fullEvent.startTime,
      endTime: fullEvent.endTime,
      allDay: fullEvent.allDay,
      organizerName: session.address.slice(0, 8),
      groupAddress: fullEvent.groupAddress,
      groupName,
    });

    for (const attendee of attendees) {
      if (attendee.address === session.address) continue; // skip self

      // 1. Create local invite record
      const inviteId = crypto.randomUUID();
      await db.calendarInvites.add({
        id: inviteId,
        eventId: fullEvent.id,
        senderAddress: session.address,
        recipientAddress: attendee.address,
        rsvp: CalendarRSVP.PENDING,
        sentAt: now,
        deliveredP2P: 0,
        eventSnapshot,
      } as any);

      // 2. Queue pending delivery for offline sync via MongoDB
      try {
        await db.queuePendingDelivery({
          recipientAddress: attendee.address,
          senderAddress: session.address,
          deliveryType: 'calendar_invite',
          metadata: {
            eventId: fullEvent.id,
            calendarAction: 'invite',
            eventTitle: fullEvent.title,
            startTime: fullEvent.startTime,
            endTime: fullEvent.endTime,
            allDay: fullEvent.allDay,
            organizerName: session.address.slice(0, 8),
            groupAddress: fullEvent.groupAddress,
            groupName,
          },
        });
        console.log('[CALENDAR_CRUD] Queued pending delivery for attendee', {
          attendee: attendee.address.slice(0, 8),
          eventId: fullEvent.id,
        });
      } catch (err) {
        console.error('[CALENDAR_CRUD] Failed to queue pending delivery:', err);
      }
    }

    // 3. Dispatch event so P2P layer can attempt direct delivery to online peers
    window.dispatchEvent(new CustomEvent('calendar-event-created', {
      detail: { event: fullEvent, inviteSnapshot: eventSnapshot },
    }));

    return fullEvent;
  }, [db, session?.address]);

  const updateEvent = useCallback(async (eventId: string, updates: Partial<CalendarEvent>): Promise<void> => {
    if (!session?.address) throw new Error('No session');
    
    const existing = await db.calendarEvents.get(eventId);
    if (!existing) throw new Error(`Event ${eventId} not found`);
    
    const existingEvent = entityToEvent(existing);
    const vectorClock = { ...existingEvent.vectorClock };
    vectorClock[session.address] = (vectorClock[session.address] || 0) + 1;
    
    const updatedEvent: CalendarEvent = {
      ...existingEvent,
      ...updates,
      id: eventId, // Prevent ID override
      vectorClock,
      updatedAt: Date.now(),
    };
    
    await db.calendarEvents.put(eventToEntity(updatedEvent) as any);
  }, [db, session?.address]);

  const deleteEvent = useCallback(async (eventId: string): Promise<void> => {
    if (!session?.address) throw new Error('No session');
    
    const existing = await db.calendarEvents.get(eventId);
    if (!existing) return; // Already gone
    
    const existingEvent = entityToEvent(existing);
    const vectorClock = { ...existingEvent.vectorClock };
    vectorClock[session.address] = (vectorClock[session.address] || 0) + 1;
    
    // Soft delete for sync
    await db.calendarEvents.update(eventId, {
      deletedAt: Date.now(),
      updatedAt: Date.now(),
      vectorClock: JSON.stringify(vectorClock),
    });
  }, [db, session?.address]);

  const respondToInvite = useCallback(async (inviteId: string, rsvp: CalendarRSVP): Promise<void> => {
    await db.calendarInvites.update(inviteId, {
      rsvp,
      respondedAt: Date.now(),
    });
    
    // Also update attendee RSVP in the event itself
    const invite = await db.calendarInvites.get(inviteId);
    if (invite) {
      const event = await db.calendarEvents.get(invite.eventId);
      if (event) {
        const attendees = JSON.parse(event.attendees) as CalendarAttendee[];
        const attendee = attendees.find(a => a.address === invite.recipientAddress);
        if (attendee) {
          attendee.rsvp = rsvp;
          attendee.respondedAt = Date.now();
          await db.calendarEvents.update(invite.eventId, {
            attendees: JSON.stringify(attendees),
            updatedAt: Date.now(),
          });
        }
      }
    }
  }, [db]);

  return { createEvent, updateEvent, deleteEvent, respondToInvite };
}

// ─── Settings ────────────────────────────────────────────────────────────────

const SETTINGS_KEY = 'moby_pref_calendarSettings';

/** Read calendar settings from localStorage */
export function getCalendarSettings(): CalendarSettings {
  try {
    const stored = localStorage.getItem(SETTINGS_KEY);
    if (stored) {
      return { ...DEFAULT_CALENDAR_SETTINGS, ...JSON.parse(stored) };
    }
  } catch { /* ignore */ }
  return { ...DEFAULT_CALENDAR_SETTINGS };
}

/** Write calendar settings to localStorage */
export function setCalendarSettings(settings: Partial<CalendarSettings>): void {
  const current = getCalendarSettings();
  const updated = { ...current, ...settings };
  localStorage.setItem(SETTINGS_KEY, JSON.stringify(updated));
}

// ─── Availability Publishing ─────────────────────────────────────────────────

/**
 * Build and publish the availability bitmap to the email gateway's MongoDB.
 * This is the ONLY calendar data that touches the server — a compact 84-byte
 * free/busy bitmap covering the next 7 days in quarter-hour slots.
 *
 * Call after any event CRUD or on a timer (e.g., every 15 min).
 */
export async function publishAvailabilityToGateway(
  db: import('@/moby/db').MobyDB,
  address: string,
): Promise<boolean> {
  const gatewayUrl = localStorage.getItem('moby_pref_emailGatewayUrl')?.trim();
  if (!gatewayUrl) return false;

  const settings = getCalendarSettings();
  if (!settings.publishAvailability) return false;

  try {
    const { buildAvailabilityFromEvents, EventVisibility } = await import('@/lib/calendar-types');

    // Fetch events within the 7-day bitmap window
    const now = Date.now();
    const windowEnd = now + 7 * 24 * 60 * 60 * 1000;

    const entities = await db.calendarEvents
      .where('startTime')
      .between(now - 24 * 60 * 60 * 1000, windowEnd) // 1-day lookback for spanning events
      .filter(e => !e.deletedAt)
      .toArray();

    // Convert entities to domain events
    const domainEvents = entities.map(e => ({
      id: e.id,
      title: e.title,
      description: e.description,
      location: e.location,
      startTime: e.startTime,
      endTime: e.endTime,
      allDay: e.allDay === 1,
      timezone: e.timezone,
      recurrence: e.recurrence ? JSON.parse(e.recurrence) : undefined,
      recurringEventId: e.recurringEventId,
      visibility: (e.visibility || 'busy') as typeof EventVisibility[keyof typeof EventVisibility],
      reminders: JSON.parse(e.reminders || '[]'),
      organizerAddress: e.organizerAddress,
      attendees: JSON.parse(e.attendees || '[]'),
      groupAddress: e.groupAddress,
      vectorClock: JSON.parse(e.vectorClock || '{}'),
      createdAt: e.createdAt,
      updatedAt: e.updatedAt,
      deletedAt: e.deletedAt,
      color: e.color,
    }));

    const bitmap = buildAvailabilityFromEvents(domainEvents as any, settings.timezone);

    const response = await fetch(`${gatewayUrl}/api/email/presence/availability`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        address,
        slots: bitmap.slots,
        timezone: bitmap.timezone,
        windowStart: bitmap.windowStart,
      }),
    });

    if (!response.ok) {
      console.warn('[CALENDAR_AVAIL] Failed to publish availability:', response.status);
      return false;
    }

    console.log('[CALENDAR_AVAIL] Availability published to gateway', {
      timezone: bitmap.timezone,
      eventCount: domainEvents.length,
    });
    return true;
  } catch (error: any) {
    console.warn('[CALENDAR_AVAIL] Error publishing availability:', error?.message || error);
    return false;
  }
}
